import { For, type JSX, Show } from "solid-js";
import { Dynamic } from "solid-js/web";
import { Link } from "@tanstack/solid-router";
import { AppBackground } from "@/components/app-background";
import { Logo } from "@/components/ui/logo";

/**
 * Habits' OWN auth shell — deliberately NOT the shared `AuthLayout`/`AuthCard`.
 *
 * The generic kit framed every auth screen as "a glass card centered inside a
 * split brand panel" — the exact silhouette every clone inherited, which is why
 * they all read as the same template. listto-app's de-clone went with a single
 * editorial *stacked column* (logo chip + headline + hairline + full-bleed form).
 *
 * Habits breaks the silhouette a DIFFERENT way, rooted in its product motif —
 * the **daily streak strip**: a full-bleed brand banner whose backdrop is a row
 * of streak cells lighting up left→right toward a crowning flame, then the form
 * laid out FLAT directly on the ambient background underneath (no glass card, no
 * split, no centered surface).
 *
 *   ┌────────────────────────────────────────────┐
 *   │ ▓▓░░▓▓░▓▓▓▓▓▓▓▓ 🔥   ← streak-strip banner   │  (full-bleed, edge to edge)
 *   │  ◳ Habits  · eyebrow ·                       │
 *   │  Big headline.                               │
 *   │  Supporting line.                            │
 *   ├────────────────────────────────────────────┤
 *   │  email   [____________]   ← flat fields,     │
 *   │  pass    [____________]      no card frame    │
 *   │  [ Sign in ]                                  │
 *   └────────────────────────────────────────────┘
 *
 * So two products open side by side never look like the same shell: listto-app is a
 * quiet centered column, Habits leads with an expressive horizontal streak hero.
 * They still share primitives (buttons, fields, SocialAuthButtons,
 * disabled-until-ready) — the difference is structural, not skin-deep.
 *
 * ── SCROLL CONTRACT (kit #13) ─────────────────────────────────────────────
 * `AppBackground` is `position: fixed; overflow: hidden` and clips its children,
 * so here it is used purely as a backdrop SIBLING. The content lives in its own
 * scroll container: `min-h-[100dvh] overflow-y-auto` (never `h-dvh`/
 * `overflow-hidden`), with a `min-h-full` inner column and `min-h-0` regions, so
 * a tall form (register) or a short landscape viewport stays fully reachable by
 * scrolling instead of being clipped.
 */

interface AuthSceneProps {
  /** Eyebrow chip (e.g. "Welcome back"). Optional. */
  eyebrow?: string;
  /** Page headline — the editorial promise for this screen. Optional for bare loading states. */
  headline?: string;
  /** Supporting line under the headline. */
  tagline?: string;
  /**
   * Heading level for the headline. The scene headline IS the page h1 for
   * Habits' auth (there is no card title). Pass "h2" only if the screen owns
   * its own h1 elsewhere.
   */
  headingLevel?: "h1" | "h2";
  /** The form (fields + actions), rendered flat below the brand banner. */
  children: JSX.Element;
  /** Footer slot (links: register / back home), rendered under the form. */
  footer?: JSX.Element;
}

// Deterministic streak-strip pattern (0 = empty .. 4 = strongest) so the banner
// reads as a real streak filling up, brightest toward the recent (right) end,
// where the flame crowns it.
const STRIP_CELLS = 24;
function stripIntensity(i: number): number {
  const base = (i * 5 + 2) % 5;
  const recencyBoost = i > STRIP_CELLS - 5 ? 2 : i > STRIP_CELLS - 11 ? 1 : 0;
  return Math.min(4, Math.max(0, base - 2 + recencyBoost));
}
function stripOpacity(level: number): number {
  return [0.1, 0.3, 0.52, 0.74, 1][level] ?? 0.1;
}

export function AuthScene(props: AuthSceneProps) {
  return (
    <>
      <AppBackground />

      <div
        class="fixed inset-0 z-10 flex w-full justify-center overflow-x-hidden overflow-y-auto"
        style={{
          "padding-top": "max(0px, env(safe-area-inset-top))",
          "padding-bottom": "max(1.5rem, env(safe-area-inset-bottom))",
          "padding-left": "max(0px, env(safe-area-inset-left))",
          "padding-right": "max(0px, env(safe-area-inset-right))",
        }}
      >
        <div class="flex min-h-full w-full max-w-md min-h-0 flex-col">
          {/* Full-bleed streak-strip brand banner — Habits' own chrome. */}
          <header class="relative overflow-hidden border-b border-border/70 px-6 pt-9 pb-7 sm:px-8">
            {/* Streak strip backdrop: cells lighting up toward a crowning flame. */}
            <div class="pointer-events-none absolute inset-x-0 top-0 flex items-center gap-1 px-6 pt-3 sm:px-8">
              <For each={Array.from({ length: STRIP_CELLS })}>
                {(_, i) => (
                  <span
                    class="h-2 flex-1 rounded-sm bg-accent"
                    style={{ opacity: stripOpacity(stripIntensity(i())) }}
                    aria-hidden="true"
                  />
                )}
              </For>
              <span class="ml-0.5 text-celebrate" aria-hidden="true">
                <svg class="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M11 1.5c2.9 3 4.6 5.5 4.6 8.7 0 1.5-.4 2.7-1.2 3.8.7-.2 1.5-.5 2.1-1.2.5 1.2.8 2.4.8 3.5 0 3.7-3 6.6-6.3 6.6S4.6 21 4.6 17.3c0-2.7 1.4-4.8 2.9-6.7.4.7.9 1.4 1.6 1.9-.4-3.2.9-6.3 3.3-10Z" />
                </svg>
              </span>
            </div>

            <div class="relative mt-4 flex flex-col gap-4">
              <div class="flex items-center justify-between gap-3">
                <Link
                  to="/"
                  class="group rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  aria-label="Habits"
                >
                  {/* Reuses the shared `Logo` (chip + wordmark) instead of a
                      one-off duplicate — see `logo.tsx`'s doc comment for why
                      the chip MUST stay solid `bg-accent`/`text-accent-foreground`. */}
                  <Logo
                    size="sm"
                    class="transition-transform group-hover:scale-105 group-active:scale-95"
                  />
                </Link>
                <Show when={props.eyebrow}>
                  <span class="rounded-full bg-accent/15 px-3 py-1 text-xs font-semibold text-accent ring-1 ring-accent/20">
                    {props.eyebrow}
                  </span>
                </Show>
              </div>

              <Show when={props.headline}>
                <div class="space-y-2">
                  <Dynamic
                    component={props.headingLevel ?? "h1"}
                    class="text-pretty text-2xl font-bold leading-tight tracking-tight text-foreground sm:text-3xl"
                  >
                    {props.headline}
                  </Dynamic>
                  <Show when={props.tagline}>
                    <p class="text-pretty text-base leading-relaxed text-muted-foreground">
                      {props.tagline}
                    </p>
                  </Show>
                </div>
              </Show>
            </div>
          </header>

          {/* Form laid out flat on the ambient background (no glass card). */}
          <div class="flex min-h-0 w-full flex-1 flex-col px-6 pt-7 sm:px-8">
            <div class="w-full">{props.children}</div>

            <Show when={props.footer}>
              <footer class="mt-7 space-y-3 text-center">{props.footer}</footer>
            </Show>
          </div>
        </div>
      </div>
    </>
  );
}
